import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import styles from '../../../RenewNonEmployeeAccess.module.scss';
import Card from '../../../../../../../../components/UI/MDC/Card/Card';
import { Headline4 } from '../../../../../../../../components/UI/MDC/Typography/Typography';
import ReviewApps from './components/ReviewApps';
import ReviewMobileDevices from './components/ReviewMobileDevices';
import ReviewBadges from './components/ReviewBadges';
import ReviewServiceAccounts from './components/ReviewServiceAccounts';
import StaffMemberInformationCard from '../../../../../../components/StaffMemberInformation/StaffMemberInformationCard';
import YesNoRadioButton from '../../../../../../components/YesNoRadioButton';
import { getDefaultRenewalDate } from '../../../_utils';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const ReviewInformation = ({
	formik,
	setDisableSubmit,
	universalRenewalDate,
	setUniversalRenewalDate,
}) => {
	const [useUniversalRenewalDate, setUseUniversalRenewalDate] =
		useState(true);

	useEffect(() => {
		//disable submit button due to universal date error
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const checkDate = new Date(universalRenewalDate);
		checkDate.setHours(0, 0, 0, 0);
		//check if date is more than 1 year from today
		const nextYear = new Date();
		nextYear.setFullYear(today.getFullYear() + 1);
		nextYear.setHours(0, 0, 0, 0);
		if (checkDate > nextYear || today > checkDate) {
			setDisableSubmit(true);
		} else {
			setDisableSubmit(false);
		}
	}, [universalRenewalDate, setDisableSubmit]);

	useEffect(() => {
		//disable/enable submit button due to individual renewal date
		let errorPresent = false;
		for (let i = 0; i < formik.values.staffMembersForReview.length; i++) {
			const staffMember = formik.values.staffMembersForReview[i];
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			const checkDate = new Date(staffMember?.newRenewalDate);
			checkDate.setHours(0, 0, 0, 0);
			const nextYear = new Date();
			nextYear.setFullYear(today.getFullYear() + 1);
			nextYear.setHours(0, 0, 0, 0);

			if (checkDate > nextYear || today > checkDate) {
				errorPresent = true;
			}
		}

		setDisableSubmit(errorPresent);
	}, [formik, setDisableSubmit]);

	useEffect(() => {
		//when the useUniversalRenewalDate radio button is toggled to 'no', reset the universal renewal date value to the default date
		if (useUniversalRenewalDate !== true) {
			setUniversalRenewalDate(getDefaultRenewalDate());
		}

		//if the radio button is toggled to 'yes', ensure that all values for individual dates are reset to the default date
		if (useUniversalRenewalDate) {
			let updatedSelected = [...formik.values.staffMembersForReview];
			updatedSelected = updatedSelected.map(x => {
				x.newRenewalDate = getDefaultRenewalDate();
				return x;
			});
			formik.setFieldValue('staffMembersForReview', updatedSelected);
		}
		// eslint-disable-next-line
	}, [useUniversalRenewalDate, setUniversalRenewalDate]);

	return (
		<Card xlarge>
			<Headline4>Review non employee staff member information</Headline4>

			{formik.values.staffMembersForReview.length > 1 ? (
				<YesNoRadioButton
					formDetails={{ validation: { touched: true } }}
					title='Will all non employees being renewed have the same renewal date/time?'
					validationMessage=''
					valid={true}
					onChange={e => {
						setUseUniversalRenewalDate(!useUniversalRenewalDate);
					}}
					value={useUniversalRenewalDate ? 'Yes' : 'No'}
				/>
			) : null}
			{useUniversalRenewalDate ||
			formik.values.staffMembersForReview.length === 1 ? (
				<RenewalDateSelect
					value={universalRenewalDate}
					handleChange={value => setUniversalRenewalDate(value)}
				/>
			) : null}
			{formik?.values?.staffMembersForReview?.map(
				(staffMember, index) => {
					return (
						<StaffMemberInfo
							staffMember={staffMember}
							index={index}
							formik={formik}
							key={staffMember?.esoFiId}
							useUniversalRenewalDate={useUniversalRenewalDate}
							setDisableSubmit={setDisableSubmit}
						/>
					);
				},
			)}
		</Card>
	);
};

export default ReviewInformation;

//internal
const StaffMemberInfo = ({
	staffMember,
	index,
	formik,
	useUniversalRenewalDate,
	setDisableSubmit,
}) => {
	const handleIndividualRenewalDateChange = value => {
		//ensure value is valid, disable submit if invalid
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const checkDate = new Date(value);
		checkDate.setHours(0, 0, 0, 0);
		//check if date is more than 1 year from today or before the current date
		const nextYear = new Date();
		nextYear.setFullYear(today.getFullYear() + 1);
		nextYear.setHours(0, 0, 0, 0);
		if (checkDate > nextYear || today > checkDate) {
			setDisableSubmit(true);
		} else {
			setDisableSubmit(false);
		}

		let updatedSelected = [...formik.values.staffMembersForReview];
		updatedSelected[index].newRenewalDate = value;
		formik.setFieldValue('staffMembersForReview', updatedSelected);
	};
	return (
		<>
			<div className={styles.margin_top_small}>
				<Accordion className={styles.accordion} defaultExpanded>
					<AccordionSummary
						className={styles.accordion_summary}
						expandIcon={
							<ArrowDownwardIcon
								className={styles.accordion_summary}
							/>
						}
						aria-controls={'StaffMember-content-' + index}
						id={'StaffMember-header-' + index}
					>
						<Typography>
							{staffMember?.lastName +
								', ' +
								staffMember?.firstName}
						</Typography>
					</AccordionSummary>
					<AccordionDetails className={styles.accordion_details}>
						<StaffMemberInformationCard
							staffMember={staffMember}
							formik={formik}
							actions={
								!useUniversalRenewalDate ? (
									<RenewalDateSelect
										staffMember={staffMember}
										value={
											staffMember?.newRenewalDate ??
											getDefaultRenewalDate()
										}
										handleChange={value =>
											handleIndividualRenewalDateChange(
												value,
											)
										}
									/>
								) : null
							}
						/>
						{/* apps */}
						<ReviewApps
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
						{/*Mobile Devices */}
						<ReviewMobileDevices
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
						{/*Badges */}
						<ReviewBadges
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
						{/*Service Accounts */}
						<ReviewServiceAccounts
							staffMember={staffMember}
							index={index}
							formik={formik}
						/>
					</AccordionDetails>
				</Accordion>
			</div>
		</>
	);
};

const RenewalDateSelect = ({
	value = getDefaultRenewalDate(),
	handleChange = e => {
		console.log(e.target.value);
	},
}) => {
	const currentYear = new Date().getFullYear();
	let maxDate = new Date();
	maxDate.setFullYear(currentYear + 1);

	return (
		<>
			<DatePicker
				label='Renewal Date'
				value={dayjs(value)}
				onChange={newValue => handleChange(newValue.toDate())}
				maxDate={dayjs(maxDate)}
				minDate={dayjs(new Date())}
				sx={{ maxWidth: '25vw' }}
			/>
		</>
	);
};
